import { useTournament } from '../context/TournamentContext';
import { useNavigate } from 'react-router-dom';
import { Clock, Trophy, ArrowRight, Zap } from 'lucide-react';
import type { MatchUp } from '../data/types';

export function BracketDiagram() {
  const { matchUps, matchUpResults, getTeam, getGamesByMatchUp } = useTournament();
  const navigate = useNavigate();

  const semi1 = matchUps.find(m => m.round === 'semi_1');
  const semi2 = matchUps.find(m => m.round === 'semi_2');
  const semi3 = matchUps.find(m => m.round === 'semi_3');
  const semi4 = matchUps.find(m => m.round === 'semi_4');
  const place7 = matchUps.find(m => m.round === 'place_7');
  const place5 = matchUps.find(m => m.round === 'place_5');
  const place3 = matchUps.find(m => m.round === 'place_3');
  const final_ = matchUps.find(m => m.round === 'final');

  /** Get the earliest tee time from a matchup's games, fallback to scheduled_time */
  function getMatchTime(mu: MatchUp | undefined): string | null {
    if (!mu) return null;
    const games = getGamesByMatchUp(mu.id);
    const teeTimes = games
      .map(g => g.tee_time)
      .filter(Boolean)
      .sort() as string[];
    return teeTimes[0] ?? mu.scheduled_time ?? null;
  }

  function getStatusInfo(mu: MatchUp | undefined) {
    if (!mu) return { statusLabel: 'TBD', cls: 'scheduled', icon: 'clock' as const, time: null as string | null };
    const result = matchUpResults.get(mu.id);
    const time = getMatchTime(mu);
    if (result?.isCompleted) {
      return { statusLabel: 'Dokončeno', cls: 'completed', icon: 'check' as const, time };
    }
    // Check if any game in this matchup has actual scores
    const games = getGamesByMatchUp(mu.id);
    const hasAnyScores = result && (result.games.some(gr => gr.matchPlayState.holesPlayed > 0));
    if (hasAnyScores) {
      return { statusLabel: 'LIVE', cls: 'live', icon: 'live' as const, time };
    }
    return { statusLabel: 'Nezačal', cls: 'scheduled', icon: 'clock' as const, time };
  }

  function StatusIcon({ type }: { type: 'clock' | 'check' | 'live' }) {
    if (type === 'live') return <Zap size={11} />;
    if (type === 'check') return <span style={{ fontSize: '0.7rem' }}>✓</span>;
    return <Clock size={11} />;
  }

  function renderMatchBox(matchUp: MatchUp | undefined, label: string, isFinal = false) {
    if (!matchUp) return (
      <div className="bk-match bk-empty">
        <div className="bk-label">{label}</div>
        <div className="bk-teams">
          <div className="bk-team"><span className="bk-dot" /><span className="bk-name">TBD</span></div>
          <div className="bk-team"><span className="bk-dot" /><span className="bk-name">TBD</span></div>
        </div>
        <div className="bk-status bk-status-scheduled">
          <span className="bk-status-time"><Clock size={11} /> —</span>
          <span className="bk-status-badge">Nezačal</span>
        </div>
      </div>
    );

    const teamA = matchUp.team_a_id ? getTeam(matchUp.team_a_id) : null;
    const teamB = matchUp.team_b_id ? getTeam(matchUp.team_b_id) : null;
    const result = matchUpResults.get(matchUp.id);
    const status = getStatusInfo(matchUp);
    const winnerTeamId = result?.winnerTeamId;
    const hasScores = result && (result.liveTeamAPoints > 0 || result.liveTeamBPoints > 0);

    return (
      <div
        className={`bk-match bk-match--${status.cls} ${isFinal ? 'bk-final' : ''}`}
        onClick={() => navigate(`/match/${matchUp.id}`)}
      >
        {/* Accent top bar */}
        <div className={`bk-accent-bar bk-accent-bar--${status.cls}`} />

        <div className="bk-label">
          {isFinal && <Trophy size={13} style={{ color: 'var(--accent)' }} />}
          {label}
        </div>

        <div className="bk-teams">
          <div className={`bk-team ${winnerTeamId === matchUp.team_a_id ? 'bk-winner' : ''}`}>
            <span className="bk-dot" style={{ background: teamA?.color ?? 'var(--primary)' }} />
            <span className="bk-name">{teamA?.short_name ?? teamA?.name ?? 'TBD'}</span>
            {hasScores && <span className="bk-score">{result!.liveTeamAPoints}</span>}
          </div>
          <div className={`bk-team ${winnerTeamId === matchUp.team_b_id ? 'bk-winner' : ''}`}>
            <span className="bk-dot" style={{ background: teamB?.color ?? 'var(--accent)' }} />
            <span className="bk-name">{teamB?.short_name ?? teamB?.name ?? 'TBD'}</span>
            {hasScores && <span className="bk-score">{result!.liveTeamBPoints}</span>}
          </div>
        </div>

        <div className={`bk-status bk-status-${status.cls}`}>
          <span className="bk-status-time">
            <Clock size={11} />
            {status.time ?? '—'}
          </span>
          <span className={`bk-status-badge bk-status-badge--${status.cls}`}>
            <StatusIcon type={status.icon} />
            {status.statusLabel}
          </span>
        </div>
      </div>
    );
  }

  function ConnectorArrow() {
    return (
      <div className="bk-connector">
        <div className="bk-connector-line" />
        <ArrowRight size={16} className="bk-connector-icon" />
        <div className="bk-connector-line" />
      </div>
    );
  }

  return (
    <div className="bracket-container">
      {/* Section: Lower bracket (5-8) */}
      <div className="bk-section">
        <div className="bk-section-title">
          <span className="bk-section-icon">🎯</span>
          O 5.–8. místo
        </div>
        <div className="bk-bracket-flow">
          <div className="bk-round">
            <div className="bk-round-label">Semifinále</div>
            <div className="bk-round-matches">
              {renderMatchBox(semi1, 'Semifinále 1')}
              {renderMatchBox(semi2, 'Semifinále 2')}
            </div>
          </div>
          <ConnectorArrow />
          <div className="bk-round">
            <div className="bk-round-label">O umístění</div>
            <div className="bk-round-matches">
              {renderMatchBox(place5, 'O 5. místo')}
              {renderMatchBox(place7, 'O 7. místo')}
            </div>
          </div>
        </div>
      </div>

      {/* Section: Upper bracket (1-4) */}
      <div className="bk-section bk-section-top">
        <div className="bk-section-title bk-section-title-gold">
          <span className="bk-section-icon">🏆</span>
          O 1.–4. místo
        </div>
        <div className="bk-bracket-flow">
          <div className="bk-round">
            <div className="bk-round-label">Semifinále</div>
            <div className="bk-round-matches">
              {renderMatchBox(semi3, 'Semifinále 3')}
              {renderMatchBox(semi4, 'Semifinále 4')}
            </div>
          </div>
          <ConnectorArrow />
          <div className="bk-round">
            <div className="bk-round-label">Finále</div>
            <div className="bk-round-matches">
              {renderMatchBox(final_, 'Finále', true)}
              {renderMatchBox(place3, 'O 3. místo')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
